<template>
  <div id="accordionActivityExcluded" class="accordion">
    <div class="case-details accordion-item">
      <h2 id="headingActivityExcluded" class="accordion-header">
        <button
          aria-controls="collapseActivityExcluded"
          aria-expanded="true"
          class="accordion-button collapsed"
          data-bs-target="#collapseActivityExcluded"
          data-bs-toggle="collapse"
          type="button"
        >
          <div class="mb-0 d-flex">
            <div class="bg-secondary rounded-xs h-fit-content me-8">
              <v-icon icon="contact" size="xxl" space="p-12" />
            </div>

            <div>
              <div class="h4 ff-secondary mb-0">{{ $t("clientTour.activity.excludedTrip") }}</div>
              <div class="fw-light accordion-subtitle">{{ $t("clientTour.activity.excludedTripSubtitle") }}</div>
            </div>
          </div>
        </button>
      </h2>

      <div
        id="collapseActivityExcluded"
        aria-labelledby="headingActivityExcluded"
        class="accordion-collapse collapse show"
        data-bs-parent="#accordionActivityExcluded"
      >
        <div class="accordion-body pb-32 pb-xl-48">
          <nav class="translations-tab">
            <div id="nav-tab" class="nav nav-tabs" role="tablist">
              <v-button
                v-for="(language, index) in languages"
                :id="`nav-activity-excluded-${language.key}`"
                :key="index"
                :aria-controls="`activity-excluded-${language.key}`"
                :class="{ active: language.shortName === currentLanguage }"
                :data-bs-target="`#activity-excluded-${language.key}`"
                aria-selected="true"
                class="nav-tab-link py-8 px-16 me-8"
                data-bs-toggle="tab"
                role="tab"
                size="sm"
                variant="outline-primary"
                @click="currentLanguage = language.shortName"
              >
                {{ language.label }}
              </v-button>
            </div>
          </nav>
          <form id="activity-excluded-form" class="d-flex flex-column justify-content-between h-100">
            <fieldset v-for="language in languages" :key="language.shortName">
              <div class="form-outline" v-if="currentLanguage === language.shortName">
                <label class="label">
                  <span>{{ $t("clientTour.activity.excludedTrip") }}</span>
                </label>

                <v-html-editor
                  :id="'activity-excluded-' + language.shortName"
                  ref="activity-excluded-ref"
                  :value="excludedTrip[language.shortName]?.description"
                  @input="handleInput(language.shortName, $event)"
                  @media-uploaded="editorMedia.push($event)"
                />
              </div>
            </fieldset>

            <v-button
              :is-loading="accepted"
              :disabled="disableButton"
              class="btn-icon w-fit align-self-end mt-24"
              size="sm"
              variant="primary"
              @click="updateTemplate"
            >
              {{ $t("general.button.save") }}
              <v-icon icon="arrow-right" size="sm" space="ms-12" />
            </v-button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, computed, watch } from "vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VHtmlEditor from "@/components/vendor/htmlEditor/VHtmlEditor.vue";
import api from "@/api";
import useFormValidation from "@/helpers/form";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import Template from "@/api/objects/Template";
import useConcepts from "@/helpers/Concepts";
import { mapConcepts } from "@/helpers/ConceptHelper";
import { useToast, POSITION } from "vue-toastification";

const props = defineProps({
  agency: {
    type: String,
    required: true,
  },
  template: {
    type: Object as () => Template,
    required: true,
  },
});

const emit = defineEmits(["reloadTemplate"]);
const toast = useToast();

// Build excludedTrip as a reactive object
const excludedTrip = reactive<{ [key: string]: { description: string } }>({
  en: { description: "" },
  nl: { description: "" },
  es: { description: "" },
});

const disableButton = ref<boolean>(true);
const editorMedia = ref<any[]>([]);
const currentLanguage = ref<string>(localStorage.getItem("language") || "en");

const concepts = useConcepts();
const languages = computed(() => {
  if (concepts.concepts.value && concepts.concepts.value["app.languages"]) {
    return mapConcepts(concepts.concepts.value["app.languages"]);
  }
  return [];
});

// Helper to update disableButton state
function updateDisableButton() {
  disableButton.value = Object.values(excludedTrip).some((locale) => !locale.description || locale.description.trim() === "");
}

const setExcludedTripFromTemplate = () => {
  const excludedTripData = (props.template as any)?.excludedInformation?.translations || [];
  excludedTrip.en.description = "";
  excludedTrip.nl.description = "";
  excludedTrip.es.description = "";
  excludedTripData.forEach((translation: { locale: string; description: string }) => {
    const { locale, description } = translation;
    if (excludedTrip[locale]) {
      excludedTrip[locale].description = description;
    }
  });
};

setExcludedTripFromTemplate();

watch(() => props.template, setExcludedTripFromTemplate, { deep: true });

const form = useFormValidation("", excludedTrip);
const { accepted, toggleAccepted } = form;

const handleInput = (language: string, event: string) => {
  if (excludedTrip[language]) {
    excludedTrip[language].description = event;
  }
  updateDisableButton();
};

const updateTemplate = async () => {
  try {
    toggleAccepted();
    disableButton.value = true;

    const data = {
      name: props.template.name,
      travel_type: props.template.travelType,
      origin: props.template.originId,
      destination: props.template.destinationId,
      contact_text: props.template.contactText,
      terms_conditions_text: props.template.termsConditionsText,
      excluded_trip: excludedTrip,
    };

    const template = await api.activityService.updateActivity(props.agency, props.template.id, data);

    if (editorMedia.value.length > 0) {
      const editorMediaData = {
        media: editorMedia.value,
        model_type: "template",
        collection: "tinymce",
        model_id: props.template.id,
      };
      await api.media.confirm("s3", editorMediaData);
    }

    toast.success("Saved successfully", { position: POSITION.BOTTOM_RIGHT });
    emit("reloadTemplate");
  } catch (e: any) {
    console.error(e);
    toast.error(e?.response?.data?.message || e?.message || "An error occurred", {
      position: POSITION.BOTTOM_RIGHT,
    });
  } finally {
    toggleAccepted();
  }
};
</script>

<style scoped>
.btn-outline-primary.active {
  background-color: #d82770;
  border-color: #d82770;
  color: #fcfcfc;
  border-width: 1px;
  border-radius: 0.5rem 0.5rem 0 0;
  margin-top: 0.25rem;
  box-shadow: none;
  border-bottom: 0px;
}

.nav-tabs .nav-tab-link {
  border-width: 1px;
  border-radius: 0.5rem 0.5rem 0 0;
  margin-top: 0.25rem;
  box-shadow: none;
}

.nav-tabs {
  border-bottom: 1px solid #d82770 !important;
}
</style> 